export const DEFAULT_SHOPPING_CATEGORIES = ["Produce", "Dairy", "Bakery", "Meat", "Frozen", "Household", "Other"] as const;

export type ShoppingSourceRef = {
  type: "recipe" | "manual" | "meal_plan";
  recipeId?: string;
  recipeTitle?: string;
  ingredientId?: string;
  ingredientName?: string;
  quantityText?: string;
};

export type ShoppingCategory = {
  id: string;
  listId: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  deletedAt?: string;
};

export type ShoppingItem = {
  id: string;
  listId: string;
  categoryId: string;
  canonicalName: string;
  displayName: string;
  quantityText?: string;
  notes?: string;
  completed: boolean;
  completedAt?: string;
  completedByUserId?: string;
  createdAt: string;
  createdByUserId: string;
  updatedAt: string;
  sourceRefs: ShoppingSourceRef[];
};

export type ShoppingListSummary = {
  id: string;
  name: string;
  sharingScope: "private" | "shared" | "household";
  activeItemCount: number;
  completedItemCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ShoppingList = ShoppingListSummary & {
  categories: ShoppingCategory[];
  items: ShoppingItem[];
};

export type ShoppingItemInput = {
  displayName: string;
  canonicalName?: string;
  quantityText?: string;
  notes?: string;
  categoryId?: string;
  categoryName?: string;
  sourceRefs?: ShoppingSourceRef[];
  clientMutationId?: string;
};

export type ShoppingBulkImportInput = {
  text: string;
  clientMutationId?: string;
};

export type ShoppingBulkImportResult = {
  items: ShoppingItem[];
};

export type ShoppingSuggestion = {
  displayName: string;
  canonicalName: string;
  categoryId: string;
  categoryName: string;
  score: number;
};

export function createId(): string {
  return crypto.randomUUID();
}

export function canonicalizeShoppingName(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, " ");
}

export function normalizeShoppingDisplayName(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function sortShoppingCategories(categories: readonly ShoppingCategory[]): ShoppingCategory[] {
  return [...categories].sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name));
}

export function sortShoppingItems(items: readonly ShoppingItem[]): ShoppingItem[] {
  return [...items].sort((left, right) => {
    if (left.completed !== right.completed) {
      return left.completed ? 1 : -1;
    }
    return left.canonicalName.localeCompare(right.canonicalName) || left.createdAt.localeCompare(right.createdAt);
  });
}

export type ValidationResult = { ok: true } | { ok: false; error: string };

export function validateShoppingListName(name: string): ValidationResult {
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return { ok: false, error: "name_required" };
  }
  if (trimmed.length > 120) {
    return { ok: false, error: "name_too_long" };
  }
  return { ok: true };
}

export function validateShoppingCategoryName(name: string): ValidationResult {
  const normalized = normalizeShoppingDisplayName(name);
  if (normalized.length === 0) {
    return { ok: false, error: "category_name_required" };
  }
  if (normalized.length > 80) {
    return { ok: false, error: "category_name_too_long" };
  }
  return { ok: true };
}

export function validateShoppingItemInput(input: ShoppingItemInput): ValidationResult {
  if (normalizeShoppingDisplayName(input.displayName).length === 0) {
    return { ok: false, error: "display_name_required" };
  }
  if (normalizeShoppingDisplayName(input.displayName).length > 160) {
    return { ok: false, error: "display_name_too_long" };
  }
  if (input.categoryName !== undefined && input.categoryName.trim().length > 0) {
    return validateShoppingCategoryName(input.categoryName);
  }
  return { ok: true };
}

export function validateShoppingBulkImportInput(input: ShoppingBulkImportInput): ValidationResult {
  const lines = input.text.split(/\r?\n/).map(normalizeShoppingDisplayName).filter((line) => line.length > 0);
  if (lines.length === 0) {
    return { ok: false, error: "bulk_import_items_required" };
  }
  if (lines.length > 100) {
    return { ok: false, error: "bulk_import_too_many_items" };
  }
  if (lines.some((line) => line.length > 160)) {
    return { ok: false, error: "display_name_too_long" };
  }
  return { ok: true };
}
